import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { UseContractSigningPageLogicreturnProps } from '../ContractSigningPageProps'
import { apphistory, AppState } from '../../../store'
import { HttpClient } from '../../../common/http-client'
import { Environment } from '../../../environment/app-settings'
import { SMS_CODE_STATUSES } from '../../../store/reducers/contract-sign-reducer'
import { SET_CURRENT_FULL_SMS_CODE, SMS_CODE_VALIDATION_ERROR } from '../../../store/actions/code-actions'
import { AppLinks } from '../../../environment/app-links'
import applicationService from '../../../service/application.service'
import { SignResponse, SMS_CODE_STATUSES_FROM_ALPHA } from '../../../models/responses/sing-application.response'
import { invokeLoadingWithText } from '../../../common/invokeLoading'
import useTextContents from '../../../hooks/useTextContents'
import { sleep } from '../../../common/helpers/helpers'
import { useAppSnackbar } from '../../../components/molecules/AppSnackbar/AppSnackbarProvider'
import { APP_SNACKBAR_VARIANT } from '../../../components/molecules/AppSnackbar/AppSnackbarProps'
import analyticsService from '../../../service/analytics.service'

/**
 * Hooks obsługujący logikę głównego komponentu strony z podpisaniem umowy.
 */
const useContractSigningPageLogic = (): UseContractSigningPageLogicreturnProps   => {
   const dispatch = useDispatch()
   const textContent = useTextContents()
   const ALPHA_NOT_READY_RESEND_MAX_RETRIES = 6
   const ALPHA_NOT_READY_ERROR_RETRY_DELAY = 5000

   const contractSignState = useSelector((state: AppState) => state.contractSignState)
   const ANALYTICS_EVENT_TYPES_NAMES = useSelector((state: AppState) => state.lenConfigState.analyticsEventTypesNames)
   const [blockButton, setButtonBlock] = useState(false)
   const [isSubmitting, setIsSubmitting] = useState(false)
   const { showSnackbar } = useAppSnackbar()

   useEffect(() => {
      const sendContractSigningScreenDisplayedEvent = async () => {
         try {
            await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.ContractSigningScreenDisplayed)
         }
         catch (e) {
            console.warn("Nie udało się zapisać eventu wyświetlenia ekranu podpisania umowy", e)
         }
      }

      void sendContractSigningScreenDisplayedEvent()
   }, [ANALYTICS_EVENT_TYPES_NAMES?.ContractSigningScreenDisplayed])


   const handleSignApplicationSuccess = () => {
      dispatch(invokeLoadingWithText(textContent('loaders.default')))
   }

   const handleSignApplicationError = async (signApplicationResponse: SignResponse, verificationCode: string) => {
      await sleep(1000) // Przytrzymujemy użytkownika na ekranie, na wpadek gdyby request wykonał się za szybko, tak aby nie było efektu migania ekranu

      switch(signApplicationResponse.code){ // Obsługa specyficznych kodów błędów z Backendu
         case SMS_CODE_STATUSES_FROM_ALPHA.CLIENT_PESEL_RESERVED: { //Klient ma zastrzeżony numer PESEL
            dispatch({
               type: SET_CURRENT_FULL_SMS_CODE,
               payload: verificationCode,
            })

            apphistory.push(AppLinks.clientPeselReservedPage)
            break;
         }
         default: {
            if (signApplicationResponse.code === SMS_CODE_STATUSES_FROM_ALPHA.WRONG_SMS_CODE) {
               try {
                  await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.SmsCodeIncorrect)
               }
               catch (e) {
                  console.warn("Nie udało się zapisać eventu niepoprawnego kodu SMS", e)
               }
            }

            dispatch({
               type: SMS_CODE_VALIDATION_ERROR,
               payload: SMS_CODE_STATUSES.VALIDATION_ERROR_WRONG_CODE
            })
         }
      }
   }

   const trySignApplication = async (verificationCode: string, retryCount = 0 ):  Promise<void> => {
      const signApplicationResponse = await applicationService.signApplication({ smsCode: verificationCode })

      if (signApplicationResponse.isOk) {
         setIsSubmitting(false)

         try {
            await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.SmsCodeCorrect)
         }
         catch (e) {
            console.warn("Nie udało się zapisać eventu poprawnego kodu SMS", e)
         }

         handleSignApplicationSuccess()
         
         await sleep(1000) // Przytrzymujemy użytkownika na ekranie, na wpadek gdyby request wykonał się za szybko, tak aby nie było efektu migania ekranu

         return
      }

      if(signApplicationResponse.code === SMS_CODE_STATUSES_FROM_ALPHA.ALPHA_NOT_READY) {
         if (retryCount >= ALPHA_NOT_READY_RESEND_MAX_RETRIES) {
            setIsSubmitting(false)
            showSnackbar("Błąd podczas podpisywania umowy kodem sms, spróbuj ponownie", APP_SNACKBAR_VARIANT.ERROR, Environment.DEFAULT_SNACKBAR_AUTO_HIDE_DURATION_IN_MS)

            return
         }
       

         await sleep(ALPHA_NOT_READY_ERROR_RETRY_DELAY)
         return trySignApplication(verificationCode, retryCount + 1)
      }

      setIsSubmitting(false)
      await handleSignApplicationError(signApplicationResponse, verificationCode)
   }


   // >>>>> HANDLERS
   const downloadAgreement = () => {
      setButtonBlock(true);
      const client = new HttpClient(Environment.apiUrl)
      client.download("files", true).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], {type: "application/octet-stream"}))
        const a = document.createElement("a")
   
        a.href = url;
        a.download = contractSignState.agreemenFileName
        document.body.appendChild(a)
        a.click();
        setTimeout(function(){
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        }, 100)
        
        setButtonBlock(false)
      })
   }

   const handleOnContractSigningSubmit = async() => {
      const verificationCode = Object.values(contractSignState.verificationCodeInputs).join("").trim()


      if(verificationCode.length < Object.keys(contractSignState.verificationCodeInputs).length){        
         dispatch({ 
            type: SMS_CODE_VALIDATION_ERROR, 
            payload: SMS_CODE_STATUSES.VALIDATION_ERROR_FULL_CODE_REQUIRED 
         })

         return
      }

      try {
         setIsSubmitting(true)

         await trySignApplication(verificationCode)
      }
      catch (e) {
         setIsSubmitting(false)

         dispatch({
            type: SMS_CODE_VALIDATION_ERROR,
            payload: SMS_CODE_STATUSES.SEND_ERROR
         })
      }
   }
   
  
   return {
      downloadAgreement,
      handleOnContractSigningSubmit,
      dispatch,

      contractSignState,
      isSubmitting
   }
}

export default useContractSigningPageLogic